import { Component, Input, OnInit, OnDestroy } from "@angular/core";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { Subscription } from "rxjs";
import { Branch } from "src/app/models/branch/branch";
import { Case } from "src/app/models/case/case";
import { AccountTransactionFilter } from "src/app/models/filters/account-transaction-filter";
import { FilterAreas } from "src/app/models/filters/filter-areas";
import { RootAccountBalanceDTO } from "src/app/models/account/root-account-balance-dto";
import { CurrencySubTypes } from "src/app/models/enums/currency-sub-types";
import { Currency } from "src/app/models/enums/currency";
import { AccountTransactionCreateModalComponent } from "src/app/ui/components/account-transactions/account-transaction-create-modal/account-transaction-create-modal.component";
import { AlertService } from "src/app/services/alert.service";
import { CaseService } from "src/app/services/case.service";
import { AccountTransactionService } from "src/app/services/account-transaction.service";
import { CaseTransferModalComponent } from "src/app/ui/components/cases/case-transfer-modal/case-transfer-modal.component";
import { BaseComponent } from "src/app/shared/components/base/base.component";

@Component({
  selector: "app-case-detail-panel",
  templateUrl: "./case-detail-panel.component.html",
  styleUrls: ["./case-detail-panel.component.scss"],
})
export class CaseDetailPanelComponent extends BaseComponent implements OnInit, OnDestroy {
  @Input() case: Case;

  activeAccount = 0;
  branch: Branch = JSON.parse(localStorage.getItem("currentBranch"));
  Currency = Currency;
  branchBalanceList: RootAccountBalanceDTO[] = null;
  totalBalanceHas: number;
  showScaleBalance = false;
  refreshSubscription: Subscription;

  caseTransactionFilter: AccountTransactionFilter = {
    branchId: this.branch.id,
    currency: Currency.Tl,
  };

  filterAreas: FilterAreas = {
    case: false,
    subType: true,
    customer: true,
    state: true,
    startDate: true,
    amount: true,
    endDate: true,
    creater: true,
    clearAll: true,
  };

  constructor(
    private caseService: CaseService,
    private accountTransactionService: AccountTransactionService,
    private modalService: NgbModal,
    private alertService: AlertService
  ) {
    super();
  }

  ngOnInit() {
    this.caseTransactionFilter.caseId = this.case.id;
    this.loadBranchBalanceList();
    this.refreshSubscription = this.accountTransactionService.refreshIndex.subscribe(() =>
      this.loadBranchBalanceList()
    );
  }

  ngOnDestroy() {
    this.refreshSubscription?.unsubscribe();
  }

  loadBranchBalanceList() {
    this.accountTransactionService
      .getCaseBalances({ caseId: this.case.id, branchId: this.branch.id })
      .toPromise()
      .then((res) => {
        this.branchBalanceList = res.data.balanceList;
        this.totalBalanceHas = res.data.totalBalanceHas;
      });
  }

  loadTransactionsByCurrency(currency: Currency) {
    this.showScaleBalance = currency == Currency.Gold || currency == Currency.GoldNew ? true : false;
    this.caseTransactionFilter.amount = null;
    this.caseTransactionFilter.type = null;
    this.caseTransactionFilter.customerId = null;
    this.caseTransactionFilter.currency = currency;
    this.caseTransactionFilter.beginDate = null;
    this.caseTransactionFilter.endDate = null;
    this.filterAreas.subType = this.hasSubTypeFilterAreas(currency);
  }

  deleteCase() {
    if (!this.branchBalanceList) {
      this.alertService.showDeleteAlert().then((result) => {
        if (result.isConfirmed) {
          this.caseService
            .delete(this.case.id)
            .toPromise()
            .then(() => {
              this.alertService.showSuccess();
              this.caseService.refreshIndex.next();
            });
        }
      });
    } else {
      const modalRef = this.modalService.open(CaseTransferModalComponent);
      modalRef.componentInstance.model.sourceCaseId = this.case.id;
      modalRef.componentInstance.deleteAndTransfer = true;
    }
  }

  transferCase() {
    const modalRef = this.modalService.open(CaseTransferModalComponent);
    modalRef.componentInstance.model.sourceCaseId = this.case.id;
    modalRef.componentInstance.deleteAndTransfer = false;
  }

  openAccountTransactionAddModal(currency: Currency, subType: CurrencySubTypes) {
    const modalRef = this.modalService.open(AccountTransactionCreateModalComponent);
    modalRef.componentInstance.branchPreloaded = this.branch;
    modalRef.componentInstance.currencyPreloaded = currency;
    modalRef.componentInstance.subTypePreloaded = subType;
    modalRef.componentInstance.isCustomerTransaction = false;
  }

  hasSubTypeFilterAreas(currency: Currency) {
    switch (currency) {
      case Currency.Gold:
      case Currency.Silver:
      case Currency.GoldNew:
      case Currency.Other:
      case Currency.Accessories:
      case Currency.OtherAccessories:
        return true;
      default:
        return false;
    }
  }

  getTotalBalanceHas(balance: RootAccountBalanceDTO): number {
    return balance.subTypeBalances.reduce((sum, current) => sum + current.balanceHas, 0);
  }

  getTotalBalance(balance: RootAccountBalanceDTO): number {
    return balance.subTypeBalances.reduce((sum, current) => sum + current.balance, 0);
  }

  getTotalBalanceOfScales(balance: RootAccountBalanceDTO): number {
    return balance.subTypeBalances.reduce((sum, current) => sum + (current.balanceOfScales || 0), 0);
  }
}
